/**
 * Knowledge-applied summary chip.
 *
 * One chip per artifact (not per citation): "N citations" with a hover
 * tooltip that lists the title + `kind · scope · source` for each entry.
 * Used by both the artifact list (left rail) and the SQL preview drawer so
 * the surface looks identical wherever it appears.
 *
 * Design notes:
 *  - Light surface + secondary-coloured border so the count stays readable;
 *    the default MUI `color="secondary"` chip tints the whole pill and
 *    the label disappeared on darker themes.
 *  - The tooltip overrides the default dark MUI surface — inherited dim text
 *    on a darker tooltip is barely readable; switching to the paper surface
 *    with the theme's primary text colour matches the rest of the UI.
 */

import { Box, ButtonBase, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';

import { useActiveDataset } from '../context/ActiveDatasetContext';
import { useKnowledgeBrowserStore } from '../state/knowledgeBrowserStore';
import { isOperationalGlobalRule, type EpiAppliedKnowledge } from '../types/epi-chat-types';

interface KnowledgeCitationsChipProps {
  entries: EpiAppliedKnowledge[];
  /** Optional id used for e2e selectors. */
  testId?: string;
}

const isConceptGroupEntry = (entry: EpiAppliedKnowledge): boolean => entry.kind === 'concept_group';

export const KnowledgeCitationsChip = ({ entries, testId }: KnowledgeCitationsChipProps) => {
  const openBrowser = useKnowledgeBrowserStore((s) => s.open);
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);
  // TODO: cleanup with concept_group_prevalence_preview ff
  const activeDataset = useActiveDataset();
  // Operational standing rules (warehouse quirks, schema-introspection
  // conventions, agent-behavior rules) are recalled by the agent every
  // session but the epi has no decision to make about them — surfacing them
  // as "citations" implies they are domain findings the epi could dismiss,
  // which is misleading. Filter them out at render time so the chip count
  // and tooltip both stay focused on entries the epi actually has agency
  // over (clinical defs, dataset findings, snippets, concept groups).
  const visibleEntries = entries.filter((entry) => !isOperationalGlobalRule(entry));
  if (visibleEntries.length === 0) return null;
  const label = `${visibleEntries.length} citation${visibleEntries.length === 1 ? '' : 's'}`;
  const handleEntryClick = (entry: EpiAppliedKnowledge) => {
    if (isConceptGroupEntry(entry)) {
      setOpenConceptGroupId(entry.id);
      return;
    }
    openBrowser(entry.id);
  };
  const handleChipClick = () => {
    const first = visibleEntries[0];
    if (!first) return;
    if (isConceptGroupEntry(first)) {
      setOpenConceptGroupId(first.id);
      return;
    }
    openBrowser(first.id);
  };
  return (
    <Tooltip
      placement="top-start"
      enterDelay={200}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'flip',
              enabled: true,
              options: { fallbackPlacements: ['bottom-start', 'top', 'bottom'] },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: { boundary: 'viewport', padding: 8 },
            },
          ],
        },
      }}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: (theme) => theme.palette.background.paper,
            color: (theme) => theme.palette.text.primary,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            boxShadow: 3,
            maxWidth: 360,
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: 1,
          },
        },
      }}
      title={
        <Box>
          <Typography variant="caption" fontWeight={600} display="block" sx={{ marginBottom: 0.5 }}>
            Knowledge applied
          </Typography>
          <Stack spacing={0.75}>
            {visibleEntries.map((entry) => (
              <ButtonBase
                key={entry.id}
                onClick={() => handleEntryClick(entry)}
                data-testid={`knowledge-citation-entry-${entry.id}`}
                sx={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: 0.5,
                  borderRadius: 0.5,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <Typography variant="caption" fontWeight={600} display="block">
                  {entry.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {entry.kind} · {entry.scope}
                  {entry.source ? ` · ${entry.source}` : ''}
                </Typography>
              </ButtonBase>
            ))}
          </Stack>
        </Box>
      }
    >
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <Chip
          size="small"
          label={label}
          variant="outlined"
          color="secondary"
          clickable
          onClick={handleChipClick}
          data-testid={testId}
          sx={{
            height: 22,
            fontSize: (theme) => theme.typography.caption.fontSize,
            backgroundColor: (theme) => theme.palette.background.paper,
            color: (theme) => theme.palette.text.primary,
            borderColor: (theme) => theme.palette.secondary.main,
          }}
        />
        {openConceptGroupId && (
          <ConceptGroupDetailsDialog
            conceptGroupId={openConceptGroupId}
            datasetReleaseId={activeDataset?.id ?? null}
            datasetReleaseDisplayName={activeDataset?.displayName ?? null}
            onClose={() => setOpenConceptGroupId(null)}
          />
        )}
      </Box>
    </Tooltip>
  );
};
